import React, { useState } from 'react';
import { X, Save, Loader2, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { superAdminAPI } from '../../services/api';
import { showErrorToast } from '../../utils/errorHandling';
import { KatexPreview } from '../ui/KatexPreview';

const QuestionReviewModal = ({ isOpen, question, onClose, onReviewComplete }) => {
  const [reviewData, setReviewData] = useState({
    status: '',
    feedback: '',
    suggestedChanges: '',
    rating: 5
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const statusOptions = [
    { value: 'APPROVED', label: 'Approve', icon: CheckCircle, color: 'text-green-600' },
    { value: 'NEEDS_WORK', label: 'Needs Work', icon: AlertTriangle, color: 'text-yellow-600' },
    { value: 'NOT_USABLE', label: 'Not Usable', icon: XCircle, color: 'text-red-600' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setReviewData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!reviewData.status) {
      newErrors.status = 'Please select a review status';
    }
    
    if (reviewData.status === 'NEEDS_WORK' && !reviewData.suggestedChanges.trim()) {
      newErrors.suggestedChanges = 'Please provide suggested changes for improvement';
    }
    
    if (reviewData.status === 'NOT_USABLE' && !reviewData.feedback.trim()) {
      newErrors.feedback = 'Please provide feedback explaining why this question is not usable';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      await superAdminAPI.reviewQuestion(question.id, {
        ...reviewData,
        reviewedAt: new Date().toISOString()
      });
      
      onReviewComplete();
    } catch (error) {
      console.error('Error submitting review:', error);
      showErrorToast(error, 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setReviewData({
        status: '',
        feedback: '',
        suggestedChanges: '',
        rating: 5
      });
      setErrors({});
      onClose();
    }
  };

  if (!isOpen || !question) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Review Question</h2>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Question Details */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <span className="text-sm font-medium text-gray-600">Subject:</span>
                <p className="text-sm text-gray-900">{question.subject}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Topic:</span>
                <p className="text-sm text-gray-900">{question.topic}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Difficulty:</span>
                <p className="text-sm text-gray-900">{question.difficulty}</p>
              </div>
            </div>
            
            <div className="mb-4">
              <span className="text-sm font-medium text-gray-600">Question:</span>
              <div className="mt-1 p-3 bg-white rounded border">
                <KatexPreview content={question.text || question.questionText} />
              </div>
            </div>

            {question.options && question.options.length > 0 && (
              <div>
                <span className="text-sm font-medium text-gray-600">Options:</span>
                <div className="mt-1 space-y-2">
                  {question.options.map((option, index) => (
                    <div
                      key={index}
                      className={`p-2 rounded border ${
                        option.isCorrect ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
                      }`}
                    >
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-600 mr-2">
                          {index + 1}.
                        </span>
                        <KatexPreview content={option.text} />
                        {option.isCorrect && (
                          <CheckCircle className="h-4 w-4 text-green-600 ml-2" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {question.explanation && (
              <div className="mt-4">
                <span className="text-sm font-medium text-gray-600">Explanation:</span>
                <div className="mt-1 p-3 bg-white rounded border">
                  <KatexPreview content={question.explanation} />
                </div>
              </div>
            )}
          </div>

          {/* Review Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Review Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Review Decision *
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {statusOptions.map((option) => {
                  const IconComponent = option.icon;
                  return (
                    <label
                      key={option.value}
                      className={`relative flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50 ${
                        reviewData.status === option.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="status"
                        value={option.value}
                        checked={reviewData.status === option.value}
                        onChange={handleInputChange}
                        className="sr-only"
                        disabled={loading}
                      />
                      <IconComponent className={`h-5 w-5 mr-3 ${option.color}`} />
                      <span className="text-sm font-medium text-gray-900">
                        {option.label}
                      </span>
                    </label>
                  );
                })}
              </div>
              {errors.status && <p className="mt-1 text-sm text-red-600">{errors.status}</p>}
            </div>

            {/* Quality Rating */}
            <div>
              <label htmlFor="rating" className="block text-sm font-medium text-gray-700 mb-2">
                Quality Rating (1-10)
              </label>
              <select
                id="rating"
                name="rating"
                value={reviewData.rating}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={loading}
              >
                {[...Array(10)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1} - {i + 1 <= 3 ? 'Poor' : i + 1 <= 6 ? 'Average' : i + 1 <= 8 ? 'Good' : 'Excellent'}
                  </option>
                ))}
              </select>
            </div>

            {/* Suggested Changes (for NEEDS_WORK) */}
            {reviewData.status === 'NEEDS_WORK' && (
              <div>
                <label htmlFor="suggestedChanges" className="block text-sm font-medium text-gray-700 mb-2">
                  Suggested Changes *
                </label>
                <textarea
                  id="suggestedChanges"
                  name="suggestedChanges"
                  value={reviewData.suggestedChanges}
                  onChange={handleInputChange}
                  rows={4}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.suggestedChanges ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Provide specific suggestions for improving this question..."
                  disabled={loading}
                />
                {errors.suggestedChanges && (
                  <p className="mt-1 text-sm text-red-600">{errors.suggestedChanges}</p>
                )}
              </div>
            )}

            {/* Feedback (for NOT_USABLE) */}
            {reviewData.status === 'NOT_USABLE' && (
              <div>
                <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-2">
                  Feedback *
                </label>
                <textarea
                  id="feedback"
                  name="feedback"
                  value={reviewData.feedback}
                  onChange={handleInputChange}
                  rows={4}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.feedback ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Explain why this question is not usable..."
                  disabled={loading}
                />
                {errors.feedback && (
                  <p className="mt-1 text-sm text-red-600">{errors.feedback}</p>
                )}
              </div>
            )}

            {/* General Feedback (optional) */}
            <div>
              <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-2">
                Additional Feedback (Optional)
              </label>
              <textarea
                id="feedback"
                name="feedback"
                value={reviewData.feedback}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Any additional comments or feedback..."
                disabled={loading}
              />
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Submit Review
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default QuestionReviewModal;
